import { useAccount, useReadContract, useReadContracts } from 'wagmi'
import { ADDRESSES, IS_LIVE, ZERO_ADDR } from '@/lib/contracts'
import { ERC20_ABI } from '@/lib/erc20'
import vaultFactoryAbi from '@/lib/abis/VaultFactory.json'
import v3VaultAbi from '@/lib/abis/V3Vault.json'
import v4VaultAbi from '@/lib/abis/V4Vault.json'
import rebalanceGuardAbi from '@/lib/abis/RebalanceGuard.json'
import { useHorizonPriceUsd } from '@/hooks/useHorizonPriceUsd'
import { toNumber, POLL_SLOW } from '@/lib/utils'
import { PRICE_PRECISION, CLEARING_TWAP_VALUE_ABI, clearingAddressOrZero, priceVaultPosition } from '@/lib/vaultPricing'


const NOT_DEPLOYED = !IS_LIVE || (ADDRESSES.VAULT_FACTORY || '').toLowerCase() === ZERO_ADDR

// V3-only: pool()/fee(). V4-only: poolKey() (fee lives inside it, no standalone V4 fee() getter -
// confirmed against the ABI). Everything else below is IVaultCore-shared, identical on both.
const V3_ONLY_FIELDS = ['pool', 'fee']
const V4_ONLY_FIELDS = ['poolKey']
const SHARED_FIELDS = [
  'name', 'symbol', 'token0', 'token1', 'getTotalAmounts', 'ranges', 'rangeCount', 'nav',
  'totalSupply', 'depositsPaused', 'rebalancingPaused', 'globalPause', 'strategist', 'clearing',
  'rebalanceGuard', 'feeManager', 'MAX_RANGES', 'shapeHalfWidth', 'shapePositionCount', 'callerBountyBps',
  'rehypoEnabled', 'rehypoFloorBps',
]

/// Single-vault counterpart to useVaultsList - same staged-reads shape and TWAP-derived pricing
/// (see priceVaultPosition's own doc comment), but with the richer field set a detail page needs
/// (strategist/clearing/rebalanceGuard/feeManager/shape config/pool identity) that the listing
/// page doesn't fetch for every row. Checks VaultFactory.isVault() first so an unknown address in
/// the URL resolves to an honest "not found" state instead of a blank/crashing page.
export function useVaultDetail(address) {
  const { address: user } = useAccount()
  const { rate: horizonUsd, found: hasHorizonPrice } = useHorizonPriceUsd()
  const usdxAddr = (ADDRESSES.USDX || '').toLowerCase()
  const horizonAddr = (ADDRESSES.HORIZON_TOKEN || '').toLowerCase()
  const enabled = !NOT_DEPLOYED && !!address

  const { data: isVault, isLoading: loadingIsVault } = useReadContract({
    address: ADDRESSES.VAULT_FACTORY, abi: vaultFactoryAbi, functionName: 'isVault', args: [address],
    query: { enabled, refetchInterval: POLL_SLOW },
  })

  const { data: isV4Vault } = useReadContract({
    address: ADDRESSES.VAULT_FACTORY, abi: vaultFactoryAbi, functionName: 'isV4Vault', args: [address],
    query: { enabled: enabled && !!isVault, refetchInterval: POLL_SLOW },
  })
  const venue = isV4Vault ? 'v4' : 'v3'
  const venueKnown = isV4Vault !== undefined
  const abi = isV4Vault ? v4VaultAbi : v3VaultAbi
  const venueOnlyFields = isV4Vault ? V4_ONLY_FIELDS : V3_ONLY_FIELDS

  const fields = [...SHARED_FIELDS, ...venueOnlyFields]
  const { data: coreReads } = useReadContracts({
    query: { enabled: enabled && !!isVault && venueKnown, refetchInterval: POLL_SLOW },
    contracts: [
      ...fields.map((functionName) => ({ address, abi, functionName })),
      ...(user ? [{ address, abi, functionName: 'balanceOf', args: [user] }] : []),
    ],
  })
  const coresKnown = !!coreReads
  const core = {}
  fields.forEach((f, i) => { core[f] = coreReads?.[i]?.result })
  const userShares = user ? coreReads?.[fields.length]?.result : undefined

  const t0 = core.token0?.toLowerCase()
  const t1 = core.token1?.toLowerCase()
  const tokenAddrs = coresKnown ? [t0, t1].filter(Boolean) : []

  const { data: tokenReads } = useReadContracts({
    query: { enabled: tokenAddrs.length === 2, refetchInterval: POLL_SLOW },
    contracts: tokenAddrs.flatMap((addr) => [
      { address: addr, abi: ERC20_ABI, functionName: 'symbol' },
      { address: addr, abi: ERC20_ABI, functionName: 'decimals' },
    ]),
  })
  const meta0 = tokenReads ? { symbol: tokenReads[0]?.result, decimals: tokenReads[1]?.result ?? 18 } : { symbol: '…', decimals: 18 }
  const meta1 = tokenReads ? { symbol: tokenReads[2]?.result, decimals: tokenReads[3]?.result ?? 18 } : { symbol: '…', decimals: 18 }

  const { data: unitPriceRaw } = useReadContract({
    address: clearingAddressOrZero(core.clearing), abi: CLEARING_TWAP_VALUE_ABI, functionName: 'twapValue',
    args: [address, PRICE_PRECISION, 0n],
    query: { enabled: coresKnown && !!core.clearing && core.clearing !== ZERO_ADDR, refetchInterval: POLL_SLOW },
  })

  // The REAL rebalance incentive lives on the RebalanceGuard, not on the vault's flat
  // `callerBountyBps` (which is 0 whenever the Dutch-auction curve is active - the default). Read the
  // curve so the detail page shows what a keeper actually earns instead of the dormant flat field.
  const { data: bountyRead } = useReadContracts({
    query: { enabled: coresKnown && !!core.rebalanceGuard && core.rebalanceGuard !== ZERO_ADDR, refetchInterval: POLL_SLOW },
    contracts: [
      { address: core.rebalanceGuard, abi: rebalanceGuardAbi, functionName: 'bountyCurves', args: [address] },
    ],
  })
  const bc = bountyRead?.[0]?.result
  const bountyCurve = bc ? {
    configured: !!(bc.configured ?? bc[0]),
    startBps: Number(bc.startBps ?? bc[1] ?? 0),
    endBps: Number(bc.endBps ?? bc[2] ?? 0),
    rampSeconds: Number(bc.rampSeconds ?? bc[3] ?? 0),
  } : null

  const isLoading = loadingIsVault || (!!isVault && (!venueKnown || !coresKnown || !tokenReads))

  if (NOT_DEPLOYED || loadingIsVault) {
    return { vault: null, isLoading: loadingIsVault, notFound: false, notDeployed: NOT_DEPLOYED }
  }
  if (!isVault) {
    return { vault: null, isLoading: false, notFound: true, notDeployed: false }
  }
  if (isLoading) {
    return { vault: null, isLoading: true, notFound: false, notDeployed: false }
  }

  const amt0 = toNumber(core.getTotalAmounts?.[0] ?? 0n, meta0.decimals)
  const amt1 = toNumber(core.getTotalAmounts?.[1] ?? 0n, meta1.decimals)

  const { price0, price1, priced0, priced1, tvlUsd, fullyPriced } = priceVaultPosition({
    t0, t1, decimals0: meta0.decimals, decimals1: meta1.decimals, amt0, amt1,
    usdxAddr, horizonAddr, hasHorizonPrice, horizonUsd, unitPriceRaw,
  })

  const totalSupply = core.totalSupply ?? 0n
  const sharesOwned = userShares ?? 0n
  const userSharePct = totalSupply > 0n ? toNumber(sharesOwned) / toNumber(totalSupply) : 0
  const userTvlUsd = tvlUsd * userSharePct

  return {
    isLoading: false,
    notFound: false,
    notDeployed: false,
    vault: {
      address,
      venue,
      name: core.name || `${meta0.symbol}/${meta1.symbol}`,
      symbol: core.symbol,
      token0: { address: core.token0, ...meta0, amount: amt0, price: price0, priced: priced0 },
      token1: { address: core.token1, ...meta1, amount: amt1, price: price1, priced: priced1 },
      tvlUsd,
      fullyPriced,
      ranges: core.ranges ?? [],
      rangeCount: Number(core.rangeCount ?? 0),
      maxRanges: Number(core.MAX_RANGES ?? 0),
      shapeHalfWidth: core.shapeHalfWidth ?? 0,
      shapePositionCount: Number(core.shapePositionCount ?? 0),
      callerBountyBps: Number(core.callerBountyBps ?? 0),
      // Dutch-auction rebalance bounty curve from the RebalanceGuard (start -> end over ramp seconds
      // of eligibility). `null` until read; `configured: false` means the vault falls back to the
      // flat callerBountyBps above.
      bountyCurve,
      rehypoEnabled: !!core.rehypoEnabled,
      rehypoFloorBps: Number(core.rehypoFloorBps ?? 0),
      navToken1: core.nav ?? 0n,
      totalSupply,
      depositsPaused: !!core.depositsPaused,
      rebalancingPaused: !!core.rebalancingPaused,
      globalPause: !!core.globalPause,
      strategist: core.strategist,
      clearing: core.clearing,
      rebalanceGuard: core.rebalanceGuard,
      feeManager: core.feeManager,
      // V3-only pool()/fee(); V4-only poolKey() (whose own .fee field substitutes) - see this
      // file's own top-of-file note on the asymmetry.
      pool: venue === 'v3' ? core.pool : undefined,
      feeTier: venue === 'v3' ? core.fee : core.poolKey?.fee,
      poolKey: venue === 'v4' ? core.poolKey : undefined,
      hasPosition: !!user && sharesOwned > 0n,
      userShares: sharesOwned,
      userSharePct,
      userTvlUsd,
    },
  }
}
